import EventHubMixin from '@/mixins/EventHub.js'
import APIMixin from '@/mixins/API.js'
import SizesMixin from '@/mixins/Sizes.js'
import TimeMixin from '@/mixins/Time.js'

import AppPicture from '@/components/app-picture'
import AppVideo from '@/components/app-video'
import AppIconSound from '@/components/app-icon-sound'

export default
{
    name: 'app-player',

    mixins:
    [
        EventHubMixin,
        APIMixin,
        SizesMixin,
        TimeMixin
    ],

    components:
    {
        AppPicture,
        AppVideo,
        AppIconSound
    },

    data()
    {
        return {
            active: false,
            content: null,
            playing: false,
            playedOnce: false,
            progress: 0,
            elapsed: '00:00',
            duration: '00:00',
            fullscreenEnable: document.fullscreenEnabled || document.mozFullScreenEnabled || document.webkitFullscreenEnabled,
            fullscreenActive: false,
            mutedActive: false
        }
    },

    watch:
    {
        $route()
        {
            this.active = false
        },

        content()
        {
            if(this.content)
            {
                this.$nextTick(() =>
                {
                    this.setVideo()
                })
            }
        }
    },

    mounted()
    {
        // this.fullscreenEnable = false
        this.fullscreenCallback = () =>
        {
            const fullScreenElement = document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement
            this.fullscreenActive = fullScreenElement
        }
        document.addEventListener('fullscreenchange', this.fullscreenCallback)
        document.addEventListener('webkitfullscreenchange', this.fullscreenCallback)
        document.addEventListener('mozfullscreenchange', this.fullscreenCallback)
        document.addEventListener('MSFullscreenChange', this.fullscreenCallback)

        // Activate / Deactivate events
        this.$eventHub.$on('player.activate', this.activate)
        this.$eventHub.$on('player.deactivate', this.deactivate)

        // Header trigger click when active
        this.$eventHub.$on('headerTrigger.click', (data) =>
        {
            if(data.action === 'deactivatePlayer')
            {
                this.deactivate()
            }
        })

        /**
         * Keyboard
         */
        this.keydown = (event) =>
        {
            switch(event.keyCode)
            {
                // Space
                case 32:
                    event.preventDefault()
                    if(this.$video)
                    {
                        this.$video.toggle()
                    }
                    break

                // Right
                case 39:
                    event.preventDefault()
                    if(this.$video)
                    {
                        this.$video.$video.currentTime += 10
                    }
                    break

                // Left
                case 37:
                    event.preventDefault()
                    if(this.$video)
                    {
                        this.$video.$video.currentTime -= 10
                    }
                    break

                // F
                // Fullscreen
                case 70:
                    event.preventDefault()
                    this.onToggleFullscreen()
                    break

                // M
                // Mute
                case 77:
                    event.preventDefault()
                    this.onToggleSound()
                    break
            }
        }
    },

    methods:
    {
        activate(playerId)
        {
            // Reset
            this.content = null
            this.$video = null
            this.playing = false
            this.playedOnce = false
            this.progress = 0
            this.elapsed = '00:00'
            this.duration = '00:00'

            // Fetch page content
            this.fetch(`videos/${playerId}`, { per_page: 1000 }, (data) =>
            {
                this.content = data

                // Player
                this.$eventHub.$emit('player.onLoaded')

                this.$nextTick(() =>
                {
                    // Line
                    this.$eventHub.$emit('line.setElement', this.$refs.lineMark, true)
                })
            })

            // Header trigger
            this.$eventHub.$emit('headerTrigger.setState', { state: 'close', event: { action: 'deactivatePlayer' } })

            // Header logo
            this.$eventHub.$emit('headerLogo.setState', { state: 'hidden' })

            // Audio
            this.$eventHub.$emit('audio.pause', false)
            this.$eventHub.$emit('audio.deactivate')

            // Player
            this.$eventHub.$emit('player.onActivate')

            // Keyboard event
            document.addEventListener('keydown', this.keydown)

            // Lock scroll
            this.$eventHub.$emit('main.lockScroll')

            // Activate
            this.active = true
        },

        deactivate()
        {
            // Unlock scroll
            this.$eventHub.$emit('main.unlockScroll')

            // Header trigger
            this.$eventHub.$emit('headerTrigger.prevState')

            // Header logo
            this.$eventHub.$emit('headerLogo.prevState')

            // Audio
            this.$eventHub.$emit('audio.endPause')
            this.$eventHub.$emit('audio.activate')

            // Player
            this.$eventHub.$emit('player.onDeactivate')

            // Keyboard event
            document.removeEventListener('keydown', this.keydown)

            // Deactivate
            this.active = false
        },

        setVideo()
        {
            // Video
            this.$video = this.$refs.video
            this.mutedActive = this.$video.$video.muted

            this.$video.$on('play', () =>
            {
                this.playing = true

                if(!this.playedOnce)
                {
                    this.playedOnce = true
                }
            })

            this.$video.$on('pause', () =>
            {
                this.playing = false
            })

            this.$video.$on('volumechange', () =>
            {
                this.mutedActive = this.$video.$video.muted
            })

            this.$video.$on('time-change', ($video) =>
            {
                if(!this.seekBar.gesture.down)
                {
                    this.progress = $video.currentTime / $video.duration
                }
            })

            this.$video.$on('ended', () =>
            {
                this.playedOnce = false
            })

            // Seekbar
            this.seekBar = {}
            this.seekBar.$element = this.$refs.seekBar
            this.seekBar.$full = this.$refs.seekBarFull
            this.seekBar.boundings = null
            this.seekBar.boundingsNeedsUpdate = true
            this.seekBar.x = 0

            this.seekBar.gesture = {}
            this.seekBar.gesture.down = false

            // Touch
            this.seekBar.gesture.touchstart = (event) =>
            {
                event.preventDefault()

                this.seekBar.gesture.down = true

                // Pause video
                this.$video.pause()

                // Update boudings
                if(this.seekBar.boundingsNeedsUpdate)
                {
                    this.seekBar.boundings = this.seekBar.$full.getBoundingClientRect()
                    this.seekBar.boundingsNeedsUpdate = false
                }

                // Update progress
                this.seekBar.x = event.touches[0].pageX
                this.progress = Math.min(Math.max((this.seekBar.x - this.seekBar.boundings.left) / this.seekBar.boundings.width, 0), 1)

                // Add touchmove and touchend event listeners
                window.addEventListener('touchmove', this.seekBar.gesture.touchmove, { passive: false })
                window.addEventListener('touchend', this.seekBar.gesture.touchend, { passive: false })
            }

            this.seekBar.gesture.touchmove = (event) =>
            {
                event.preventDefault()

                // Update progress
                this.seekBar.x = event.touches[0].pageX
                this.progress = Math.min(Math.max((this.seekBar.x - this.seekBar.boundings.left) / this.seekBar.boundings.width, 0), 1)
            }

            this.seekBar.gesture.touchend = () =>
            {
                this.seekBar.gesture.down = false

                this.$video.goTo(this.progress)
                this.$video.play()

                // Remove touchmove and touchend event listeners
                window.removeEventListener('touchmove', this.seekBar.gesture.touchmove)
                window.removeEventListener('touchend', this.seekBar.gesture.touchend)
            }

            this.seekBar.$element.addEventListener('touchstart', this.seekBar.gesture.touchstart)

            // Mouse
            this.seekBar.gesture.mousedown = (event) =>
            {
                event.preventDefault()

                this.seekBar.gesture.down = true

                // Pause video
                this.$video.pause()

                // Update boudings
                if(this.seekBar.boundingsNeedsUpdate)
                {
                    this.seekBar.boundings = this.seekBar.$full.getBoundingClientRect()
                    this.seekBar.boundingsNeedsUpdate = false
                }

                // Update progress
                this.seekBar.x = event.clientX
                this.progress = Math.min(Math.max((this.seekBar.x - this.seekBar.boundings.left) / this.seekBar.boundings.width, 0), 1)

                // Add touchmove and touchend event listeners
                window.addEventListener('mousemove', this.seekBar.gesture.mousemove)
                window.addEventListener('mouseup', this.seekBar.gesture.mouseup)
            }

            this.seekBar.gesture.mousemove = (event) =>
            {
                // Update progress
                this.seekBar.x = event.clientX
                this.progress = Math.min(Math.max((this.seekBar.x - this.seekBar.boundings.left) / this.seekBar.boundings.width, 0), 1)
            }

            this.seekBar.gesture.mouseup = () =>
            {
                this.seekBar.gesture.down = false

                this.$video.goTo(this.progress)
                this.$video.play()

                // Remove touchmove and touchend event listeners
                window.removeEventListener('mousemove', this.seekBar.gesture.mousemove)
                window.removeEventListener('mouseup', this.seekBar.gesture.mouseup)
            }

            this.seekBar.$element.addEventListener('mousedown', this.seekBar.gesture.mousedown)
        },

        onToggleSound()
        {
            if(this.$video)
            {
                this.$video.toggleMute()
            }
        },

        onToggleClick()
        {
            if(this.$video)
            {
                this.$video.toggle()
            }
        },

        onToggleFullscreen()
        {
            // Fullscreen no enable
            if(!this.fullscreenEnable)
            {
                return
            }

            // Get current fullscreen element
            const fullScreenElement = document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement

            // Go fullscreen
            if(!fullScreenElement)
            {
                if(this.$el.requestFullscreen)
                {
                    this.$el.requestFullscreen()
                }
                else if(this.$el.mozRequestFullScreen)
                {
                    this.$el.mozRequestFullScreen()
                }
                else if(this.$el.webkitRequestFullscreen)
                {
                    this.$el.webkitRequestFullscreen()
                }
                else if(this.$el.msRequestFullscreen)
                {
                    this.$el.msRequestFullscreen()
                }
            }

            // Leave fullscreen
            else
            {
                if(document.exitFullscreen)
                {
                    document.exitFullscreen()
                }
                else if(document.mozCancelFullScreen)
                {
                    document.mozCancelFullScreen()
                }
                else if(document.webkitExitFullscreen)
                {
                    document.webkitExitFullscreen()
                }
            }
        },

        onResize()
        {
            if(this.seekBar)
            {
                this.seekBar.boundingsNeedsUpdate = true
            }
        },

        onTick()
        {
            if(this.$video && !isNaN(this.$video.$video.duration))
            {
                const elapsed = this.getFormatedSeconds(this.progress * this.$video.$video.duration)
                if(elapsed !== this.elapsed)
                {
                    this.elapsed = elapsed
                }

                const duration = this.getFormatedSeconds(this.$video.$video.duration)
                if(duration !== this.duration)
                {
                    this.duration = duration
                }
            }
        },

        getFormatedSeconds(_seconds)
        {
            const secondsNum = parseInt(_seconds, 10)
            let hours = Math.floor(secondsNum / 3600)
            let minutes = Math.floor((secondsNum - (hours * 3600)) / 60)
            let seconds = secondsNum - (hours * 3600) - (minutes * 60)

            if(hours < 10)
            {
                hours = `0${hours}`
            }
            if(minutes < 10)
            {
                minutes = `0${minutes}`
            }
            if(seconds < 10)
            {
                seconds = `0${seconds}`
            }

            const result = `${minutes}:${seconds}`

            return result
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-player/script.js